import { useContext, useEffect, useState } from 'react'

import { useAnalyzeKeywordsStream } from '@/common/api/modules/article'
import { KeywordAnalysisSummary, KeywordItem } from '@/common/model/media/articleCreator'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import IconButton from '@/components/view/atoms/button/IconButton'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import AiInsideArea from '@/components/view/layouts/area/AiInsideArea'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import InsideArea, { BgColor } from '@/components/view/layouts/area/InsideArea'
import Line from '@/components/view/layouts/area/Line'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Modal, { ModalType } from '@/components/view/layouts/modal/Modal'
import ModalContent, { ModalContentType } from '@/components/view/layouts/modal/ModalContent'
import ModalFooter from '@/components/view/molecules/modal/ModalFooter'
import ModalHeader from '@/components/view/molecules/modal/ModalHeader'
import AnalysisProgressStepper from '@/components/view/molecules/progress/AnalysisProgressStepper'

import { splitKeywords } from '@/components/container/form/modules/media/articleCreator/keywordAnalysisSchema'
import KeywordAnalysisTable from '@/components/container/modules/media/articleCreator/KeywordAnalysisTable'

import { KeywordSuggestionModalContext } from './KeywordSuggestionModalProvider'

// analyze_keywords_stream（backend/routers/article_creator.py）の6ステップに対応
// KeywordAnalysisTab.tsx と同じステップ定義
const PROGRESS_STEPS = [
  { label: 'サジェスト取得', icon: IconType.SEARCH },
  { label: 'サイト情報取得', icon: IconType.HOME },
  { label: 'AI分析', icon: IconType.AI_FILL },
  { label: '順位推定', icon: IconType.ANALYTICS },
  { label: '結果整理', icon: IconType.MEMO },
  { label: '履歴保存', icon: IconType.HISTORY }
]

// キーワード一覧をCSVとしてダウンロードする（クライアントサイドのみ、バックエンド呼び出し無し）
const downloadKeywordsCsv = (keywords: KeywordItem[]) => {
  const headers = ['キーワード', '月間検索数', 'CVE', '検索順位', 'URL']
  const rows = keywords.map((k) => [
    k.keyword,
    String(k.monthly_search_volume ?? 0),
    String(k.cve ?? ''),
    k.search_rank ? `${k.search_rank}位` : '',
    k.ranking_url ?? ''
  ])
  const csv = [headers, ...rows]
    .map((row) => row.map((c) => `"${c.replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'keyword_analysis.csv'
  link.click()
  URL.revokeObjectURL(url)
}

// 分析サマリー（分析キーワード数・入力キーワードを含む数・サジェスト取得数・最も機会があるキーワード）
const AnalysisSummaryCard = ({
  summary,
  suggestCount
}: {
  summary: KeywordAnalysisSummary
  suggestCount: number
}) => (
  <Area noBorder bgColor='secondary'>
    <SummaryItemList>
      <SummaryItem color='secondary'>
        <Text size={12} weight='bold'>
          分析キーワード数
        </Text>
        <Text size={24} weight='bold'>
          {summary.total_keywords}
        </Text>
      </SummaryItem>
      <SummaryItem color='secondary'>
        <Text size={12} weight='bold'>
          入力キーワードを含む数
        </Text>
        <Text size={24} weight='bold'>
          {summary.input_containing}
        </Text>
      </SummaryItem>
      <SummaryItem color='secondary'>
        <Text size={12} weight='bold'>
          サジェスト取得数
        </Text>
        <Text size={24} weight='bold'>
          {suggestCount}
        </Text>
      </SummaryItem>
      <SummaryItem color='secondary'>
        <Text size={12} weight='bold'>
          最も機会があるキーワード
        </Text>
        <Badge badgeType={BadgeType.KEYWORD_ON_BG_FILL}>{summary.best_opportunity || '-'}</Badge>
      </SummaryItem>
    </SummaryItemList>
  </Area>
)

interface ContentProps {
  siteId: number
  keyword: string
  siteUrl?: string | null
  onApply(keyword: string): void
  onClose(): void
}

// モーダルが開くたびに再マウントし、分析実行・選択状態を初期化する
const KeywordSuggestionModalContent = ({
  siteId,
  keyword,
  siteUrl,
  onApply,
  onClose
}: ContentProps) => {
  const [selected, setSelected] = useState<KeywordItem[]>([])
  const { analyzing, progress, result, error, start } = useAnalyzeKeywordsStream()

  useEffect(() => {
    start({
      site_id: siteId,
      keywords: splitKeywords(keyword),
      site_url: siteUrl || null,
      mode: 'no_gsc'
    })
    // マウント時に一度だけ分析を実行する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleApply = () => {
    if (selected.length === 0) return
    onApply(selected.map((k) => k.keyword).join(', '))
    onClose()
  }

  const keywords = result?.analysis?.keywords ?? []
  const summary = result?.analysis?.summary

  const handleApplyAll = () => {
    if (keywords.length === 0) return
    onApply(keywords.map((k) => k.keyword).join(', '))
    onClose()
  }

  return (
    <>
      <ModalContent type={ModalContentType.NO_FRAME}>
        {analyzing || (!result && !error) ? (
          <AnalysisProgressStepper
            progress={
              progress ?? {
                step: 0,
                total_steps: 6,
                percent: 0,
                message: '分析を開始しています...'
              }
            }
            steps={PROGRESS_STEPS}
          />
        ) : error ? (
          <Text size={14} color={TextColor.CRITICAL}>
            {error}
          </Text>
        ) : keywords.length > 0 ? (
          <div className='flex flex-col gap-4'>
            <AreaContent>
              <div>
                <SummaryHeading>分析サマリー</SummaryHeading>
                {summary && (
                  <AnalysisSummaryCard
                    summary={summary}
                    suggestCount={result?.suggest_count ?? 0}
                  />
                )}
              </div>
              {summary?.recommendation && (
                <AiInsideArea border>
                  <div className='flex items-start p-3'>
                    <Icon type={IconType.AI_FILL} />
                    <Text size={14} color={TextColor.AI}>
                      {summary.recommendation}
                    </Text>
                  </div>
                </AiInsideArea>
              )}
              <ItemAlign justify='center'>
                <Button
                  color={ButtonColor.SECONDARY}
                  onClick={() => downloadKeywordsCsv(keywords)}
                  size='large'
                >
                  <Icon type={IconType.DOWNLOAD} />
                  CSVダウンロード
                </Button>
              </ItemAlign>
            </AreaContent>
            <Line />
            <AreaContent>
              <div className='flex flex-col gap-1'>
                <Text weight='bold'>記事に含めるべきキーワード</Text>
                <AiInsideArea border>
                  <div className='flex items-start p-3'>
                    <Icon type={IconType.AI_FILL} />
                    <div className='flex flex-wrap gap-1'>
                      {keywords.map((k, i) => (
                        <Badge key={i} size={10} badgeType={BadgeType.KEYWORD_ON_BG_FILL}>
                          {k.keyword}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </AiInsideArea>
              </div>
              <div className='flex justify-center'>
                <Button color={ButtonColor.PRIMARY_BRAND} size='large' onClick={handleApplyAll}>
                  提案内容を反映する
                </Button>
              </div>
            </AreaContent>
            <KeywordAnalysisTable
              keywords={keywords}
              inputKeyword={keyword}
              selectedKeywords={selected}
              onSelect={setSelected}
            />
          </div>
        ) : (
          <Text size={14} color={TextColor.SECONDARY} align='center'>
            提案キーワードがありません
          </Text>
        )}
      </ModalContent>
      <ModalFooter>
        <Button color={ButtonColor.SECONDARY} size='large' onClick={onClose}>
          閉じる
        </Button>
        <Button
          color={ButtonColor.PRIMARY}
          size='large'
          disabled={selected.length === 0}
          onClick={handleApply}
        >
          選択したキーワードを反映する
        </Button>
      </ModalFooter>
    </>
  )
}

// 記事作成画面のキーワード選定用モーダル（既存のキーワード分析APIとテーブル表示を流用）
const KeywordSuggestionModal = () => {
  const { modal, showing, hide } = useContext(KeywordSuggestionModalContext)

  return (
    <Modal show={showing} type={ModalType.MIDDLE}>
      <ModalHeader text='キーワード分析結果' onClickClose={hide} />
      {modal.showing && (
        <KeywordSuggestionModalContent
          siteId={modal.siteId}
          keyword={modal.keyword}
          siteUrl={modal.siteUrl}
          onApply={modal.onApply}
          onClose={hide}
        />
      )}
    </Modal>
  )
}

export default KeywordSuggestionModal
